import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { GraduationCap, HeartPulse, Route, Users } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const TIMELINE = [
  { year: "2007", text: "Enters public service as a nominated councillor in the Nairobi City Council." },
  { year: "2013", text: "Elected Member of Parliament for Westlands County on an ODM ticket." },
  { year: "2017", text: "Re-elected for a second term; NG-CDF management rated among Nairobi's best." },
  { year: "2022", text: "Wins a third term, deepening education, health and security programmes." },
  { year: "2025", text: "Formally declares his candidacy for Governor of Bungoma County in 2027." },
];

const CATS = ["All", "Education", "Health", "Infrastructure", "Empowerment"];

const ACHIEVEMENTS = [
  { cat: "Education", icon: GraduationCap, title: "Bursaries at scale", text: "NG-CDF bursaries supporting thousands of students across Westlands' schools every year." },
  { cat: "Education", icon: GraduationCap, title: "Classrooms & schools", text: "New classrooms and refurbishments in Highridge, Parklands, Kangemi and Mountain View." },
  { cat: "Health", icon: HeartPulse, title: "Health centre upgrades", text: "Upgraded public health facilities and maternity services within the county." },
  { cat: "Health", icon: HeartPulse, title: "Medical & PWD drives", text: "Free medical camps and assistive-device programmes for persons with disabilities." },
  { cat: "Infrastructure", icon: Route, title: "Roads & lighting", text: "Tarmacked access roads, street lighting and modernized markets across the wards." },
  { cat: "Infrastructure", icon: Route, title: "Water & sanitation", text: "Water and sanitation projects reaching informal settlements in Westlands." },
  { cat: "Empowerment", icon: Users, title: "Youth ICT hubs", text: "Vocational training partnerships and digital hubs equipping young people for work." },
  { cat: "Empowerment", icon: Users, title: "Women & micro-business", text: "Grants and table-banking support for women-led and small trading groups." },
];

export const Record = () => {
  const [cat, setCat] = useState("All");
  const list = ACHIEVEMENTS.filter((a) => cat === "All" || a.cat === cat);

  return (
    <section id="record" data-testid="record-section" className="py-24 sm:py-36 bg-[#12141C]/60 border-y border-white/5">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal>
          <p className="eyebrow mb-4" data-testid="record-eyebrow">Current Role</p>
          <h2 className="font-display font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-[1.08] max-w-3xl">
            MP for Westlands County, <span className="text-orange-500">2013 — present.</span>
          </h2>
          <p className="mt-5 max-w-2xl text-base sm:text-lg text-stone-400 leading-relaxed">
            Twelve years of county delivery in Nairobi — and a legislative record
            built on inclusion, accountability and visible results.
          </p>
        </Reveal>

        <div className="mt-16 grid lg:grid-cols-[1fr_1.4fr] gap-14">
          <div data-testid="record-timeline">
            {TIMELINE.map((t, i) => (
              <Reveal key={t.year} delay={i * 0.06}>
                <div className="flex gap-6 group">
                  <div className="flex flex-col items-center">
                    <span className="mono text-sm font-semibold text-orange-500 border border-orange-500/40 rounded-full px-3 py-1 bg-[#0B0C10]">
                      {t.year}
                    </span>
                    {i < TIMELINE.length - 1 && (
                      <span className="w-px flex-1 bg-gradient-to-b from-orange-500/40 to-white/5 mt-2" />
                    )}
                  </div>
                  <p className="pb-10 text-stone-300 text-base leading-relaxed pt-1">{t.text}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <div>
            <Reveal delay={0.1}>
              <div className="flex flex-wrap gap-2 mb-8" data-testid="record-filter-bar">
                {CATS.map((c) => (
                  <button
                    key={c}
                    data-testid={`record-filter-${c.toLowerCase()}`}
                    onClick={() => setCat(c)}
                    className={`rounded-full px-4 py-2 mono text-[11px] tracking-[0.14em] uppercase transition-colors duration-200 ${
                      cat === c
                        ? "bg-orange-600 text-[#0B0C10] font-semibold"
                        : "border border-white/12 text-stone-400 hover:text-orange-400 hover:border-orange-500/50"
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </Reveal>

            <motion.div layout className="grid sm:grid-cols-2 gap-4" data-testid="record-achievements-grid">
              <AnimatePresence mode="popLayout">
                {list.map((a) => (
                  <motion.div
                    layout
                    key={a.title}
                    initial={{ opacity: 0, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.96 }}
                    transition={{ duration: 0.35, ease: "easeOut" }}
                    className="bento-card"
                    data-testid={`record-card-${a.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  >
                    <a.icon size={20} className="text-orange-500" />
                    <h3 className="font-display font-semibold text-lg mt-4">{a.title}</h3>
                    <p className="text-sm text-stone-400 leading-relaxed mt-2">{a.text}</p>
                    <p className="mono text-[10px] tracking-[0.2em] uppercase text-stone-600 mt-4">{a.cat}</p>
                  </motion.div>
                ))}
              </AnimatePresence>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};
